import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Loader2 } from "lucide-react";

const REPORTS = [
  ["total", "Total Beneficiaries"], ["eligible", "Eligible"], ["ineligible", "Ineligible"],
  ["error", "Error / Deficiency"], ["verification_pending", "Verification Pending"], ["without_bank", "Without Bank Account"],
  ["first_paid", "First Installment Paid"], ["first_pending", "First Installment Pending"],
  ["second_paid", "Second Installment Paid"], ["second_pending", "Second Installment Pending"],
  ["attendance_submitted", "Attendance Submitted"], ["attendance_not_submitted", "Attendance Not Submitted"],
  ["attendance_pending", "Attendance Verification Pending"],
];
const AREAS = [["", "All Areas"], ["taluka", "Taluka Area"], ["lmc", "Latur Municipal Corporation Area"]];

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState("eligible");
  const [area, setArea] = useState("");
  const [rows, setRows] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/reports/summary").then(({ data }) => setSummary(data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    api.get(`/reports/${report}`, { params: { area: area || undefined, page, page_size: 25, search: search || undefined } })
      .then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  }, [report, area, page, search]);

  const exp = (fmt) => downloadFile(`/reports/${report}/export/${fmt}`, `${report}.${fmt}`, { area: area || undefined, search: search || undefined })
    .then(() => toast.success("Export completed")).catch((e) => toast.error(errMsg(e)));

  if (!summary) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">Reports</h1>
        <p className="text-sm text-slate-500">अहवाल · filters, Excel / CSV / PDF and print</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {REPORTS.map(([k, l]) => (
          <button key={k} data-testid={`report-card-${k}`} onClick={() => { setReport(k); setPage(1); }}
            className={`rounded-xl border p-3 text-left ${report === k ? "border-[#0f2545] bg-[#0f2545] text-white" : "border-slate-200 bg-white hover:shadow"}`}>
            <div className="text-xl font-semibold">{summary.summary[k]}</div>
            <div className="text-[11px]">{l}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="report-area" value={area} onChange={(e) => { setArea(e.target.value); setPage(1); }} className="rounded-md border border-slate-300 px-2 py-2 text-sm">
          {AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="report-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search in report…" className="min-w-[220px] flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <button data-testid="report-xlsx" onClick={() => exp("xlsx")} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Excel</button>
        <button data-testid="report-csv" onClick={() => exp("csv")} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">CSV</button>
        <button data-testid="report-pdf" onClick={() => exp("pdf")} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">PDF</button>
        <button data-testid="report-print" onClick={() => window.print()} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Print</button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-[900px] w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600"><tr>{["ID", "Name", "Taluka", "Location", "Institution", "Eligibility", "1st", "2nd", "Attendance"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody data-testid="report-rows">
            {rows.items.map((r) => (
              <tr key={r.beneficiary_id} className="border-t border-slate-100">
                <td className="p-3 font-mono text-xs">{r.beneficiary_id}</td><td className="p-3">{r.full_name}</td>
                <td className="p-3">{r.taluka}</td><td className="p-3 text-xs">{r.location_type}</td>
                <td className="p-3 text-xs">{r.institution}</td><td className="p-3">{r.eligibility_status}</td>
                <td className="p-3">{r.first_installment_status}</td><td className="p-3">{r.second_installment_status}</td><td className="p-3">{r.attendance_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-slate-200 p-3 text-sm">
          <span data-testid="report-count">Showing {rows.items.length} of {rows.total}</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(page - 1)} className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40">Prev</button>
            <button disabled={page * 25 >= rows.total} onClick={() => setPage(page + 1)} className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-base font-semibold text-[#0f2545] md:text-lg">Location Report (Taluka / LMC)</h3>
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-left"><tr>{["Taluka", "Area", "Total", "Eligible", "1st Paid", "1st Pend", "2nd Paid", "2nd Pend", "Att. Sub"].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
            <tbody data-testid="location-report">
              {summary.location_report.map((r) => (
                <tr key={`${r.taluka}-${r.location_type}`} className="border-t border-slate-100">
                  <td className="p-2">{r.taluka}</td><td className="p-2">{r.location_type?.includes("Municipal") ? "LMC" : "Taluka"}</td>
                  <td className="p-2">{r.total}</td><td className="p-2">{r.eligible}</td><td className="p-2">{r.first_paid}</td>
                  <td className="p-2">{r.first_pending}</td><td className="p-2">{r.second_paid}</td><td className="p-2">{r.second_pending}</td><td className="p-2">{r.attendance_submitted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-base font-semibold text-[#0f2545] md:text-lg">Institution Report</h3>
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-left"><tr>{["Institution", "Total", "Eligible", "Ineligible", "Error", "1st Paid", "2nd Paid", "Att. Sub"].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
            <tbody data-testid="institution-report">
              {summary.institution_report.map((r) => (
                <tr key={r.institution} className="border-t border-slate-100">
                  <td className="p-2">{r.institution}</td><td className="p-2">{r.total}</td><td className="p-2">{r.eligible}</td>
                  <td className="p-2">{r.ineligible}</td><td className="p-2">{r.error}</td><td className="p-2">{r.first_paid}</td>
                  <td className="p-2">{r.second_paid}</td><td className="p-2">{r.attendance_submitted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
